import 'date-fns';
import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ptBRLocale from 'date-fns/locale/pt-BR';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { injectIntl } from 'react-intl';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const FilterInlineDatePicker = ({
    intl,
    label,
    tooltipTitle,
    stateName,
    changeDate,
    onChange,
    value,
    format = 'dd/MM/yyyy',
    minDate,
    maxDate,
}) => {
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const parsedMinDate = useMemo(() => minDate ? new Date(minDate) : null, [minDate]);
    const parsedMaxDate = useMemo(() => maxDate ? new Date(maxDate) : null, [maxDate]);

    const handleDateChange = useCallback((date) => {
        const callback = changeDate || onChange;
        if (callback) {
            callback(stateName, date);
        }
    }, [changeDate, onChange, stateName]);

    const renderInput = useCallback((params) => (
        <TextField {...params} fullWidth sx={{ backgroundColor: 'white' }} />
    ), []);

    return (
        <LocalizationProvider locale={ptBRLocale} dateAdapter={AdapterDateFns}>
            <Tooltip
                title={!value && !isPickerOpen ? (tooltipTitle || label) : ''}
                arrow
                disableInteractive
            >
                <div style={{ width: '100%' }}>
                    <DatePicker
                        renderInput={renderInput}
                        inputFormat={format}
                        disableToolbar
                        label={label}
                        value={value || null}
                        invalidDateMessage={intl.formatMessage({ id: 'date.pickers.invalid.date' })}
                        minDateMessage={intl.formatMessage({ id: 'date.pickers.date.error' })}
                        onChange={handleDateChange}
                        onOpen={() => setIsPickerOpen(true)}
                        onClose={() => setIsPickerOpen(false)}
                        KeyboardButtonProps={{ 'aria-label': 'change date' }}
                        {...(parsedMinDate ? { minDate: parsedMinDate } : {})}
                        {...(parsedMaxDate ? { maxDate: parsedMaxDate } : {})}
                    />
                </div>
            </Tooltip>
        </LocalizationProvider>
    );
};

FilterInlineDatePicker.propTypes = {
    intl: PropTypes.object.isRequired,
    label: PropTypes.string,
    tooltipTitle: PropTypes.string,
    stateName: PropTypes.string.isRequired,
    changeDate: PropTypes.func,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    format: PropTypes.string,
    minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

export default injectIntl(memo(FilterInlineDatePicker));
